import { useMutation, useQuery } from 'react-apollo';
import produce from 'immer';

import { MeWithCircles } from '../../../../../../graphql/queries/account';
import {
  circleCreate as circleCreateMutation,
  circleUpdate as circleUpdateMutation,
  circleDelete as circleDeleteMutation,
} from '../../../../../../graphql/mutations/circles';
import { circle as circleQuery } from '../../../../../../graphql/queries/circle';

const useCircles = ({ id } = {}) => {
  const {
    loading: loadingCircles,
    refetch: refetchCircles,
    data: { me: meWithCircles } = {},
  } = useQuery(MeWithCircles);

  const { loading: loadingCircle, data: { me: meWithCircle } = {} } = useQuery(
    circleQuery,
    {
      variables: { id },
      skip: !id,
    },
  );

  const [circleCreate] = useMutation(circleCreateMutation, {
    update: (
      store,
      {
        data: {
          circleCreate: { circle },
        },
      },
    ) => {
      const data = store.readQuery({ query: MeWithCircles });

      const updatedData = produce(data, draft => {
        draft.me.circles.total += 1;
        draft.me.circles.records = [circle, ...draft.me.circles.records];
      });

      store.writeQuery({
        query: MeWithCircles,
        data: updatedData,
      });
    },
  });
  const [circleUpdate] = useMutation(circleUpdateMutation);
  const [circleDelete] = useMutation(circleDeleteMutation);

  const onCircleCreate = async ({ name, assetImageId, contactIds }) => {
    const {
      data: {
        circleCreate: { circle },
      },
    } = await circleCreate({
      variables: {
        input: {
          name,
          asset_image: assetImageId
            ? {
                asset_image_id: assetImageId,
              }
            : undefined,
          circle_contacts: contactIds.map(contact_id => ({ contact_id })),
        },
      },
    });

    return circle;
  };

  const onCircleUpdate = async ({
    circleId,
    name,
    assetImageId,
    existingAssetImageId,
    newContactIds,
    deletedContactIds,
  }) => {
    const {
      data: {
        circleUpdate: { circle },
      },
    } = await circleUpdate({
      variables: {
        input: {
          circle_id: circleId,
          name,
          asset_image: assetImageId
            ? {
                asset_image_id: assetImageId,
                existing_asset_image_id: existingAssetImageId,
              }
            : undefined,
          create_circle_contacts: (newContactIds ?? []).map(contact_id => ({
            contact_id,
          })),
          delete_circle_contact_ids: deletedContactIds,
        },
      },
      refetchQueries: [
        {
          query: circleQuery,
          variables: {
            id: circleId,
          },
        },
      ],
    });

    return circle;
  };

  const onCircleDelete = async ({ circleId }) => {
    await circleDelete({
      variables: {
        input: {
          circle_id: circleId,
        },
      },
      update: store => {
        try {
          const data = store.readQuery({ query: MeWithCircles });

          const updatedData = produce(data, draft => {
            draft.me.circles = {
              ...draft.me.circles,
              total: draft.me.circles.total - 1,
              records: draft.me.circles.records.filter(
                circle => circle.id !== circleId,
              ),
            };
          });

          store.writeQuery({ query: MeWithCircles, data: updatedData });
        } catch {
          // Nothing to do here. Queries might not be loaded, so it's possible
          // we get an error here.
        }
      },
    });
  };

  return {
    loading: loadingCircles || loadingCircle,
    circle: meWithCircle?.circles?.records?.[0],
    circles: meWithCircles?.circles?.records ?? [],
    circlesTotal: meWithCircles?.circles?.total ?? 0,
    refetchCircles,
    onCircleCreate,
    onCircleUpdate,
    onCircleDelete,
  };
};

export default useCircles;
